import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import dataRoomApi from '../../dataRoomApi';
import useQueryParams from 'hooks/useQueryParams';
import DataRoomNdasTable from './components/DataRoomNdasTable';
import DataRoomNdasEmptyScreen from './components/DataRoomNdasEmptyScreen';
import DataRoomContext from '../../providers/DataRoom/DataRoom.context';
import DataRoomNdaTableProvider from './providers/DataRoomNdaTableProvider/DataRoomNdaTableProvider.provider';
import HelmetWrapper from 'components/HelmetWrapper';
import { Button, Input } from 'ncoded-component-library';
import PlusIcon from 'icons/Plus.icon';
import AddNdaModal from './components/AddNdaModal';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import NdaListModal from 'components/NdaListModal';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { NDATemplate } from 'models/Nda';
import OnboardingContext from '../../providers/Onboarding/Onboarding.context';
import DataRoomNdaContext from '../../providers/DataRoomNda/DataRoomNda.context';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import useDebounceSearch from 'hooks/useDebounceSearch';
import SearchIcon from 'icons/Search.icon';
import { FIRST_PAGE, PAGE_PARAM_KEYWORD } from 'data/global';
import useNdaSignersQuery from './hooks/useNdaSignersQuery';
import { useNavigate } from 'react-router-dom';
import { getDataRoomRoute } from 'utils';

import './DataRoomNdas.styles.scss';

type DataRoomNdasProps = {
  className?: string;
};

const DataRoomNdas: React.FC<DataRoomNdasProps> = (props) => {
  const { className } = props;

  const { isSuperAdmin } = useContext(CurrentUserContext);
  const {
    dataRoom: { _id, name: dataRoomName, isNativeEsignEnabled },
  } = useContext(DataRoomContext);
  const { contentCard } = useContext(OnboardingContext);
  const { dataRoomNdas } = useContext(DataRoomNdaContext);

  const activeTemplateIds = useMemo(
    () =>
      dataRoomNdas
        ?.map(({ envelopeTemplateId }) => envelopeTemplateId)
        .filter(Boolean),
    [dataRoomNdas],
  );

  const [addNdaModal, addNdaModalRef] = useCallbackRef<ModalRef>();
  const [ndaListModal, ndaListModalRef] = useCallbackRef<ModalRef>();

  const classes = classNames('data-room-ndas', 'page', className);

  const { params, setMultipleQueryParams } = useQueryParams();

  const navigate = useNavigate();

  const { name, email, searchString: searchStringParam = '' } = params;

  const { t } = useTranslation();

  const { limit, updateLimit } = useNumberOfResultsPerPage();

  const onSearch = useCallback(
    (searchString: string) =>
      setMultipleQueryParams({
        searchString,
        [PAGE_PARAM_KEYWORD]: FIRST_PAGE,
      }),
    [setMultipleQueryParams],
  );

  const { searchString, searchInputValue, onSearchInputChange } =
    useDebounceSearch({
      initialSearchString: searchStringParam,
      onSearch,
    });

  const hasActiveFilter = !!(searchString || name || email);

  const { isLoading, totalPages, totalItems, items, updateSigner, refetch } =
    useNdaSignersQuery({ dataRoomId: _id });

  const isEmpty = useMemo(
    () => !isLoading && !items?.length && !hasActiveFilter,
    [isLoading, items?.length, hasActiveFilter],
  );

  const onUploadNda = useCallback(
    async ({ templateId, name }: NDATemplate) => {
      await dataRoomApi.nda.linkNdaWithDataRoom(_id, templateId, name);
    },
    [_id],
  );

  return (
    <div className={classes}>
      <HelmetWrapper title={`${t('ndas')} - ${dataRoomName}`} />
      <h2>{t('ndas')}</h2>
      {contentCard}

      <div className="actions">
        <Input
          className="data-room-ndas__search"
          prefixNode={<SearchIcon />}
          placeholder={t('searchNameOrEmail')}
          value={searchInputValue}
          onChange={({ target: { value } }) => onSearchInputChange(value)}
        />

        {!isEmpty && (
          <NumberOfResultsPerPageFilter
            limit={limit}
            updateLimit={updateLimit}
          />
        )}

        {isSuperAdmin && (
          <>
            <Button variant="outline" onClick={ndaListModal?.open}>
              {t('connectNda')}
            </Button>
            <NdaListModal
              ref={ndaListModalRef}
              description={t('connectNondisclosureAgrementWithDataRoom')}
              activeTemplateIds={activeTemplateIds}
              onSelectNda={onUploadNda}
            />
          </>
        )}

        <Button
          icon={PlusIcon}
          onClick={
            isNativeEsignEnabled
              ? () => navigate(getDataRoomRoute(_id, 'ndas/new'))
              : addNdaModal?.open
          }
        >
          {t('addNda')}
        </Button>
        {!isNativeEsignEnabled && <AddNdaModal ref={addNdaModalRef} />}
      </div>

      {isEmpty ? (
        <DataRoomNdasEmptyScreen />
      ) : (
        <DataRoomNdaTableProvider
          signers={items}
          totalPages={totalPages}
          isLoading={isLoading}
          updateSigner={updateSigner}
          refetch={refetch}
        >
          <DataRoomNdasTable
            isLoading={isLoading}
            totalPages={totalPages}
            totalItems={totalItems}
            items={items}
            {...(hasActiveFilter && {
              emptyMessage: t('noLeadsFoundMatchingSearch'),
            })}
          />
        </DataRoomNdaTableProvider>
      )}
    </div>
  );
};

export default DataRoomNdas;
